// Settings Module - Redesigned with all integrations
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage, LANGUAGES } from '../contexts/LanguageContext';
import {
  Settings, Moon, Sun, Bell, Mail, Phone, Calendar,
  Globe, Link, Check, ChevronRight, Smartphone, User, Camera, Trash2, Upload,
  Shield, Palette, Volume2, MessageSquare, Building2, Key, Crown,
  RefreshCw, Laptop, LogOut, Lock, QrCode, Eye, EyeOff, AlertTriangle
} from 'lucide-react';
import PlansModule from './PlansModule';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Toggle = ({ enabled, onChange }) => (
  <button onClick={onChange}
    className={`w-12 h-6 rounded-full transition-colors relative flex-shrink-0 ${enabled ? 'bg-green-500' : 'bg-white/10'}`}>
    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${enabled ? 'left-7' : 'left-1'}`} />
  </button>
);

const SettingsModule = ({ user, initialSection, onModuleChange }) => {
  const { t, language, setLanguage, currentLang, LANGUAGES: LANG_LIST } = useLanguage();

  const SECTIONS = [
    { id: 'profile',       label: t('profile'),          icon: User,    emoji: '👤' },
    { id: 'plans',         label: t('plans'),             icon: Crown,   emoji: '👑' },
    { id: 'general',       label: t('appearance'),        icon: Palette, emoji: '🎨' },
    { id: 'notifications', label: t('notifications'),     icon: Bell,    emoji: '🔔' },
    { id: 'integrations',  label: t('integrationsLabel'), icon: Link,    emoji: '🔗' },
    { id: 'security',      label: t('security'),          icon: Shield,  emoji: '🔒' },
  ];
  const [settings, setSettings] = useState({
    theme: 'dark',
    whatsapp: '',
    email: '',
    notifications_enabled: true,
    reminders_enabled: true,
    marketing_enabled: false,
    news_enabled: false,
    events_enabled: true,
    ai_tips_enabled: true,
    google_calendar_connected: false,
    apple_calendar_connected: false,
  });
  const [instructions, setInstructions] = useState(null);
  const [activeSection, setActiveSection] = useState(initialSection || 'profile');
  const [calendarUrl, setCalendarUrl] = useState('');
  const [appleCalUrl, setAppleCalUrl] = useState('');
  const [calSyncing, setCalSyncing] = useState({ google: false, apple: false });
  const [calStatus, setCalStatus] = useState({});
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // ── Security state ──
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [twoFaStatus, setTwoFaStatus] = useState({ enabled: false, pending: false });
  const [twoFaSetup, setTwoFaSetup] = useState(null);
  const [twoFaCode, setTwoFaCode] = useState('');
  const [twoFaLoading, setTwoFaLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [pinForm, setPinForm] = useState({ current: '', new: '', confirm: '' });
  const [pinLoading, setPinLoading] = useState(false);

  // ── WA link state ──
  const [waLinked, setWaLinked] = useState(null);

  useEffect(() => {
    loadSettings();
    loadInstructions();
    loadProfilePhoto();
  }, [user.id]);

  useEffect(() => {
    if (activeSection === 'security') {
      loadSessions();
      load2FaStatus();
    }
    if (activeSection === 'integrations') {
      loadCalStatus();
    }
    if (activeSection === 'notifications') {
      loadWaStatus();
    }
  }, [activeSection]);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/${user.id}`);
      setSettings(s => ({ ...s, ...res.data }));
    } catch (e) {}
  };

  const loadProfilePhoto = async () => {
    try {
      const res = await axios.get(`${API}/users/${user.id}/profile-photo`);
      if (res.data.has_photo) setProfilePhoto(`data:${res.data.photo.mime_type};base64,${res.data.photo.data}`);
    } catch (e) {}
  };

  const loadInstructions = async () => {
    try {
      const res = await axios.get(`${API}/integrations/calendar-instructions`);
      setInstructions(res.data);
    } catch (e) {}
  };

  const loadCalStatus = async () => {
    try {
      const res = await axios.get(`${API}/calendar/sync-status/${user.id}`);
      setCalStatus(res.data || {});
      if (res.data?.google?.connected) setSettings(s => ({ ...s, google_calendar_connected: true }));
      if (res.data?.apple?.connected) setSettings(s => ({ ...s, apple_calendar_connected: true }));
    } catch (e) {}
  };

  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      const res = await axios.get(`${API}/security/sessions/${user.id}`);
      setSessions(res.data?.sessions || []);
    } catch (e) { setSessions([]); }
    finally { setSessionsLoading(false); }
  };

  const load2FaStatus = async () => {
    try {
      const res = await axios.get(`${API}/security/2fa/status/${user.id}`);
      setTwoFaStatus(res.data);
    } catch (e) {}
  };

  const loadWaStatus = async () => {
    try {
      const res = await axios.get(`${API}/kapso/user-link/${user.id}`);
      setWaLinked(res.data);
    } catch (e) { setWaLinked(null); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/${user.id}`, settings);
      document.documentElement.setAttribute('data-theme', settings.theme);
      toast.success('Configuración guardada');
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Solo se permiten imágenes'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Imagen máximo 5MB'); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/users/${user.id}/profile-photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Foto actualizada');
      loadProfilePhoto();
    } catch { toast.error('Error al subir la foto'); }
    setUploading(false);
  };

  const deletePhoto = async () => {
    if (!window.confirm('¿Eliminar tu foto de perfil?')) return;
    try {
      await axios.delete(`${API}/users/${user.id}/profile-photo`);
      setProfilePhoto(null);
      toast.success('Foto eliminada');
    } catch { toast.error('Error'); }
  };

  // ── Calendar real iCal sync ──
  const syncCalendar = async (type) => {
    const url = type === 'google' ? calendarUrl : appleCalUrl;
    if (!url) { toast.error('Ingresa la URL del calendario (.ics)'); return; }
    setCalSyncing(s => ({ ...s, [type]: true }));
    try {
      const res = await axios.post(`${API}/calendar/sync-ical`, {
        user_id: user.id,
        ical_url: url,
        calendar_type: type,
      });
      toast.success(res.data.message || 'Calendario sincronizado');
      setSettings(s => ({ ...s, [`${type}_calendar_connected`]: true }));
      if (type === 'google') setCalendarUrl('');
      else setAppleCalUrl('');
      loadCalStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al sincronizar');
    }
    setCalSyncing(s => ({ ...s, [type]: false }));
  };

  const reSync = async (type) => {
    const url = calStatus[type]?.ical_url;
    if (!url) { toast.error('URL no disponible'); return; }
    setCalSyncing(s => ({ ...s, [type]: true }));
    try {
      const res = await axios.post(`${API}/calendar/sync-ical`, {
        user_id: user.id, ical_url: url, calendar_type: type,
      });
      toast.success(res.data.message || 'Actualizado');
      loadCalStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al re-sincronizar');
    }
    setCalSyncing(s => ({ ...s, [type]: false }));
  };

  // ── Security ──
  const revokeSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/security/sessions/${user.id}/${sessionId}`);
      toast.success('Sesión cerrada');
      loadSessions();
    } catch { toast.error('Error'); }
  };

  const revokeAllSessions = async () => {
    if (!window.confirm('¿Cerrar todas las sesiones excepto la actual?')) return;
    try {
      await axios.delete(`${API}/security/sessions/${user.id}/all`);
      toast.success('Todas las sesiones cerradas');
      setSessions([]);
    } catch { toast.error('Error'); }
  };

  const setup2FA = async () => {
    setTwoFaLoading(true);
    try {
      const res = await axios.get(`${API}/security/2fa/setup/${user.id}`);
      setTwoFaSetup(res.data);
    } catch { toast.error('Error al iniciar 2FA'); }
    setTwoFaLoading(false);
  };

  const verify2FA = async () => {
    if (!twoFaCode || twoFaCode.length < 6) { toast.error('Ingresa el código de 6 dígitos'); return; }
    setTwoFaLoading(true);
    try {
      await axios.post(`${API}/security/2fa/verify/${user.id}`, { code: twoFaCode });
      toast.success('2FA activado correctamente');
      setTwoFaStatus({ enabled: true, pending: false });
      setTwoFaSetup(null);
      setTwoFaCode('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Código incorrecto');
    }
    setTwoFaLoading(false);
  };

  const disable2FA = async () => {
    if (!window.confirm('¿Desactivar la autenticación de 2 factores?')) return;
    try {
      await axios.delete(`${API}/security/2fa/${user.id}`);
      toast.success('2FA desactivado');
      setTwoFaStatus({ enabled: false, pending: false });
      setTwoFaSetup(null);
    } catch { toast.error('Error'); }
  };

  const changePin = async () => {
    if (!pinForm.current) { toast.error('Ingresa tu contraseña actual'); return; }
    if (pinForm.new.length < 4) { toast.error('La nueva contraseña debe tener al menos 4 caracteres'); return; }
    if (pinForm.new !== pinForm.confirm) { toast.error('Las contraseñas no coinciden'); return; }
    setPinLoading(true);
    try {
      await axios.post(`${API}/security/change-pin/${user.id}`, { current_pin: pinForm.current, new_pin: pinForm.new });
      toast.success('Contraseña actualizada');
      setPinForm({ current: '', new: '', confirm: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cambiar contraseña');
    }
    setPinLoading(false);
  };

  const requestWaCode = async () => {
    try {
      await axios.post(`${API}/whatsapp/send-link-code/${user.id}`);
      toast.success('Código enviado por WhatsApp');
    } catch { toast.error('Error al enviar código'); }
  };

  return (
    <div className="p-4 md:p-6 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Settings className="text-gray-400" size={24} />Configuración
        </h1>
        <p className="text-gray-500 text-sm mt-0.5">Personaliza tu experiencia OkBot</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar nav */}
        <div className="lg:w-56 flex-shrink-0">
          <div className="glass-panel p-2 rounded-2xl">
            {SECTIONS.map(s => {
              const Icon = s.icon;
              return (
                <button key={s.id} onClick={() => setActiveSection(s.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-sm text-left ${activeSection === s.id ? 'bg-red-500/20 text-red-400' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}>
                  <span>{s.emoji}</span>
                  <span>{s.label}</span>
                  {activeSection === s.id && <ChevronRight size={14} className="ml-auto" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          {/* Profile */}
          {activeSection === 'profile' && (
            <div className="glass-panel p-6 rounded-2xl space-y-5">
              <h2 className="text-white font-semibold flex items-center gap-2"><User size={18} className="text-gray-400" />Tu Perfil</h2>

              <div className="flex items-center gap-5 p-5 bg-white/5 rounded-2xl">
                <div className="relative flex-shrink-0">
                  {profilePhoto ? (
                    <img src={profilePhoto} alt="Tu foto" className="w-20 h-20 rounded-full object-cover border-4 border-red-500/30" />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center border-4 border-red-500/30">
                      <span className="text-3xl font-bold text-white">{user?.okuser?.charAt(0).toUpperCase() || 'U'}</span>
                    </div>
                  )}
                  <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    className="absolute -bottom-1 -right-1 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors shadow-lg">
                    <Camera size={14} />
                  </button>
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold text-lg">{user?.okuser || 'Usuario'}</h3>
                  <p className="text-gray-500 text-sm">{user?.email || 'Sin email'}</p>
                  <p className="text-gray-600 text-xs mt-1">Plan: {user?.plan || 'Básico'}</p>
                  {profilePhoto && (
                    <button onClick={deletePhoto} className="mt-2 flex items-center gap-1 text-red-400 hover:text-red-300 text-xs transition-colors">
                      <Trash2 size={12} />Eliminar foto
                    </button>
                  )}
                </div>
              </div>

              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />

              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 bg-white/5 rounded-xl">
                  <label className="text-gray-500 text-xs mb-1 block">Usuario</label>
                  <p className="text-white font-medium">{user?.okuser}</p>
                </div>
                <div className="p-4 bg-white/5 rounded-xl">
                  <label className="text-gray-500 text-xs mb-1 block">Email registrado</label>
                  <p className="text-white font-medium">{user?.email || '—'}</p>
                </div>
              </div>

              <p className="text-gray-600 text-xs">Foto JPG, PNG o WebP. Máximo 5MB.</p>
            </div>
          )}

          {/* Plans */}
          {activeSection === 'plans' && (
            <div className="glass-panel p-6 rounded-2xl overflow-auto">
              <PlansModule currentUser={user} />
            </div>
          )}

          {/* Appearance */}
          {activeSection === 'general' && (
            <div className="glass-panel p-6 rounded-2xl space-y-5">
              <h2 className="text-white font-semibold flex items-center gap-2"><Palette size={18} className="text-gray-400" />{t('appearance')}</h2>

              {/* Theme toggle */}
              <div className="p-4 bg-white/5 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {settings.theme === 'dark' ? <Moon className="text-blue-400" size={20} /> : <Sun className="text-yellow-400" size={20} />}
                  <div>
                    <p className="text-white font-medium">Tema</p>
                    <p className="text-gray-500 text-xs">{settings.theme === 'dark' ? 'Modo oscuro' : 'Modo claro'}</p>
                  </div>
                </div>
                <Toggle enabled={settings.theme === 'dark'} onChange={() => setSettings(s => ({ ...s, theme: s.theme === 'dark' ? 'light' : 'dark' }))} />
              </div>

              {/* Language — opens global picker modal */}
              <div className="p-4 bg-white/5 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{currentLang.flag}</span>
                  <div>
                    <p className="text-white font-medium text-sm">{t('language')}</p>
                    <p className="text-gray-500 text-xs">{currentLang.nativeName} · {t('languageDesc')}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (typeof window.openLangPicker === 'function') window.openLangPicker();
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium bg-red-500/15 hover:bg-red-500/25 text-red-400 border border-red-500/20 transition-all"
                >
                  <Globe size={14} />
                  {t('language')}
                </button>
              </div>

              <button onClick={saveSettings} disabled={saving}
                className="btn-primary w-full flex items-center justify-center gap-2">
                {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Check size={16} />}
                {saving ? t('saving') : t('saveChanges')}
              </button>
            </div>
          )}

          {/* Notifications */}
          {activeSection === 'notifications' && (
            <div className="space-y-4">
              <div className="glass-panel p-6 rounded-2xl space-y-4">
                <h2 className="text-white font-semibold flex items-center gap-2"><Bell size={18} className="text-gray-400" />Notificaciones</h2>

                {[
                  { key: 'notifications_enabled', label: 'Notificaciones generales', desc: 'Activa o desactiva todas las notificaciones', icon: Bell },
                  { key: 'reminders_enabled', label: 'Recordatorios', desc: 'Alertas de medicamentos, citas y eventos', icon: Bell },
                  { key: 'events_enabled', label: 'Eventos del calendario', desc: 'Notificaciones antes de cada evento', icon: Calendar },
                  { key: 'news_enabled', label: 'Noticias y resúmenes', desc: 'Resumen diario de las noticias que te interesan', icon: Globe },
                  { key: 'ai_tips_enabled', label: 'Tips de OkBot', desc: 'Consejos personalizados de tu asistente IA', icon: MessageSquare },
                  { key: 'marketing_enabled', label: 'Novedades y ofertas', desc: 'Actualizaciones de OkBot y ofertas especiales', icon: Bell },
                ].map(({ key, label, desc, icon: Icon }) => (
                  <div key={key} className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-red-500/15 rounded-xl flex items-center justify-center">
                        <Icon size={16} className="text-red-400" />
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">{label}</p>
                        <p className="text-gray-500 text-xs">{desc}</p>
                      </div>
                    </div>
                    <Toggle enabled={!!settings[key]} onChange={() => setSettings(s => ({ ...s, [key]: !s[key] }))} />
                  </div>
                ))}

                <div className="space-y-3">
                  <div className="p-4 bg-white/5 rounded-xl">
                    <label className="text-gray-500 text-xs mb-2 block flex items-center gap-1.5">
                      <Mail size={12} />Email de notificaciones
                    </label>
                    <input type="email" value={settings.email || ''} onChange={e => setSettings(s => ({ ...s, email: e.target.value }))}
                      placeholder="tu@email.com" className="input-glass w-full text-sm" />
                  </div>
                </div>

                <button onClick={saveSettings} disabled={saving}
                  className="btn-primary w-full flex items-center justify-center gap-2">
                  {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Check size={16} />}
                  {saving ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>

              {/* WhatsApp link card */}
              <div className="glass-panel p-5 rounded-2xl bg-gradient-to-br from-green-600/10 to-emerald-700/5 border border-green-500/20">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 bg-green-500/20 rounded-xl flex items-center justify-center">
                    <Smartphone className="text-green-400" size={20} />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">Alertas por WhatsApp</p>
                    <p className="text-gray-500 text-xs">Recibe notificaciones directamente en tu WhatsApp</p>
                  </div>
                  {waLinked?.linked && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-green-500/20 text-green-400 font-medium">✓ Vinculado</span>
                  )}
                </div>

                {waLinked?.linked ? (
                  <div className="p-3 bg-green-500/10 rounded-xl border border-green-500/20">
                    <p className="text-green-400 text-sm font-medium">WhatsApp vinculado correctamente</p>
                    <p className="text-gray-500 text-xs mt-0.5">Número OkBot: {waLinked.okbot_phone || '+52 1 55 OKBOT'}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-gray-400 text-sm">
                      Vincula tu WhatsApp para recibir alertas de recordatorios, eventos y novedades directamente en tu teléfono.
                    </p>
                    <button
                      onClick={requestWaCode}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl w-full justify-center font-semibold text-sm text-white transition-all"
                      style={{ background: 'linear-gradient(135deg,#25D366,#128C7E)' }}
                    >
                      <Smartphone size={16} />
                      📱 Pedir código por WhatsApp
                    </button>
                    <p className="text-gray-600 text-xs text-center">
                      Te enviaremos un código al número: {settings.whatsapp || 'configura tu número arriba'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Integrations */}
          {activeSection === 'integrations' && (
            <div className="space-y-4">
              <div className="glass-panel p-5 rounded-2xl">
                <h2 className="text-white font-semibold flex items-center gap-2 mb-4">
                  <Link size={18} className="text-gray-400" />Integraciones
                </h2>
                <p className="text-gray-500 text-sm">Conecta tus servicios favoritos con OkBot para automatizar tu vida.</p>
              </div>

              {/* Google Calendar */}
              <div className="glass-panel p-5 rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 bg-blue-500/20 rounded-xl flex items-center justify-center">
                      <Calendar className="text-blue-400" size={20} />
                    </div>
                    <div>
                      <p className="text-white font-medium">Google Calendar</p>
                      <p className="text-gray-500 text-xs">Sincroniza tu agenda de Google</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${calStatus.google?.connected ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/15 text-yellow-400'}`}>
                    {calStatus.google?.connected ? '✓ Conectado' : 'No conectado'}
                  </span>
                </div>

                {calStatus.google?.connected ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-green-500/10 rounded-xl border border-green-500/20">
                      <p className="text-green-400 text-sm font-medium flex items-center gap-1.5"><Check size={14} />Google Calendar sincronizado</p>
                      <p className="text-gray-500 text-xs mt-0.5">{calStatus.google.event_count} eventos · Última sync: {calStatus.google.last_synced ? new Date(calStatus.google.last_synced).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</p>
                    </div>
                    <button
                      onClick={() => reSync('google')}
                      disabled={calSyncing.google}
                      className="w-full flex items-center justify-center gap-2 text-sm py-2 rounded-xl bg-blue-500/15 hover:bg-blue-500/25 text-blue-300 border border-blue-500/20 transition-all"
                    >
                      <RefreshCw size={14} className={calSyncing.google ? 'animate-spin' : ''} />
                      {calSyncing.google ? 'Sincronizando...' : 'Sincronizar ahora'}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {instructions?.google_calendar && (
                      <div className="p-3 bg-white/5 rounded-xl">
                        <p className="text-gray-400 text-xs mb-2 font-medium">Instrucciones para obtener tu URL iCal:</p>
                        <ol className="text-gray-500 text-xs space-y-1">
                          {instructions.google_calendar.steps.map((step, i) => <li key={i}>{i + 1}. {step}</li>)}
                        </ol>
                      </div>
                    )}
                    <input type="url" value={calendarUrl} onChange={e => setCalendarUrl(e.target.value)}
                      placeholder="URL del calendario iCal (*.ics)" className="input-glass w-full text-sm" />
                    <button
                      onClick={() => syncCalendar('google')}
                      disabled={calSyncing.google}
                      className="btn-primary w-full text-sm flex items-center justify-center gap-2"
                    >
                      {calSyncing.google ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Calendar size={15} />}
                      {calSyncing.google ? 'Importando eventos...' : 'Conectar Google Calendar'}
                    </button>
                  </div>
                )}
              </div>

              {/* Apple Calendar */}
              <div className="glass-panel p-5 rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 bg-gray-500/20 rounded-xl flex items-center justify-center">
                      <span className="text-xl">🍎</span>
                    </div>
                    <div>
                      <p className="text-white font-medium">Apple Calendar (iCloud)</p>
                      <p className="text-gray-500 text-xs">Sincroniza tu agenda de iCloud</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${calStatus.apple?.connected ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/15 text-yellow-400'}`}>
                    {calStatus.apple?.connected ? '✓ Conectado' : 'No conectado'}
                  </span>
                </div>

                {calStatus.apple?.connected ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-green-500/10 rounded-xl border border-green-500/20">
                      <p className="text-green-400 text-sm font-medium flex items-center gap-1.5"><Check size={14} />iCloud Calendar sincronizado</p>
                      <p className="text-gray-500 text-xs mt-0.5">{calStatus.apple.event_count} eventos · Última sync: {calStatus.apple.last_synced ? new Date(calStatus.apple.last_synced).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</p>
                    </div>
                    <button
                      onClick={() => reSync('apple')}
                      disabled={calSyncing.apple}
                      className="w-full flex items-center justify-center gap-2 text-sm py-2 rounded-xl bg-gray-500/15 hover:bg-gray-500/25 text-gray-300 border border-gray-500/20 transition-all"
                    >
                      <RefreshCw size={14} className={calSyncing.apple ? 'animate-spin' : ''} />
                      {calSyncing.apple ? 'Sincronizando...' : 'Sincronizar ahora'}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {instructions?.apple_calendar && (
                      <div className="p-3 bg-white/5 rounded-xl">
                        <p className="text-gray-400 text-xs mb-2 font-medium">Instrucciones para obtener tu URL iCal:</p>
                        <ol className="text-gray-500 text-xs space-y-1">
                          {instructions.apple_calendar.steps.map((step, i) => <li key={i}>{i + 1}. {step}</li>)}
                        </ol>
                      </div>
                    )}
                    <input type="url" value={appleCalUrl} onChange={e => setAppleCalUrl(e.target.value)}
                      placeholder="URL de suscripción iCal" className="input-glass w-full text-sm" />
                    <button
                      onClick={() => syncCalendar('apple')}
                      disabled={calSyncing.apple}
                      className="btn-primary w-full text-sm flex items-center justify-center gap-2"
                    >
                      {calSyncing.apple ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <span>🍎</span>}
                      {calSyncing.apple ? 'Importando eventos...' : 'Conectar Apple Calendar'}
                    </button>
                  </div>
                )}
              </div>

              {/* WhatsApp */}
              <div className="glass-panel p-5 rounded-2xl bg-gradient-to-br from-green-600/10 to-emerald-700/5 border border-green-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 bg-green-500/20 rounded-xl flex items-center justify-center">
                    <Smartphone className="text-green-400" size={20} />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">Chat por WhatsApp</p>
                    <p className="text-gray-500 text-xs">Chatea con OkBot desde tu WhatsApp personal</p>
                  </div>
                </div>
                <p className="text-gray-400 text-sm mb-4">
                  Vincula tu número de WhatsApp para acceder a todas tus funciones de OkBot directamente desde WhatsApp — recordatorios, finanzas, notas y más.
                </p>
                <button
                  onClick={() => { if (onModuleChange) onModuleChange('integrations'); }}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition-colors"
                >
                  <Smartphone size={14} />
                  Ir a Configuración de WhatsApp
                </button>
              </div>

              {/* Google Workspace */}
              <div className="glass-panel p-5 rounded-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <Building2 className="text-blue-400" size={20} />
                  </div>
                  <div>
                    <p className="text-white font-medium">Google Workspace</p>
                    <p className="text-gray-500 text-xs">Docs, Sheets, Drive — próximamente</p>
                  </div>
                </div>
                <div className="p-4 bg-blue-500/5 rounded-xl border border-blue-500/20 text-center">
                  <span className="text-2xl">🚀</span>
                  <p className="text-blue-400 text-sm font-medium mt-1">Próximamente</p>
                  <p className="text-gray-500 text-xs mt-0.5">Integración con Google Drive, Docs y Sheets</p>
                </div>
              </div>

              {/* API Keys */}
              <div className="glass-panel p-5 rounded-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 bg-purple-500/20 rounded-xl flex items-center justify-center">
                    <Key className="text-purple-400" size={20} />
                  </div>
                  <div>
                    <p className="text-white font-medium">APIs Externas</p>
                    <p className="text-gray-500 text-xs">Conecta servicios externos con tu cuenta</p>
                  </div>
                </div>
                <div className="p-4 bg-purple-500/5 rounded-xl border border-purple-500/20 text-center">
                  <p className="text-purple-400 text-sm">Configura desde el Panel de Administrador</p>
                </div>
              </div>
            </div>
          )}

          {/* Security */}
          {activeSection === 'security' && (
            <div className="space-y-4">
              {/* Change password */}
              <div className="glass-panel p-6 rounded-2xl space-y-4">
                <h2 className="text-white font-semibold flex items-center gap-2"><Lock size={18} className="text-gray-400" />Cambiar contraseña</h2>
                <div className="space-y-3">
                  <div className="relative">
                    <input
                      type={showPin ? 'text' : 'password'}
                      value={pinForm.current}
                      onChange={e => setPinForm(f => ({ ...f, current: e.target.value }))}
                      placeholder="Contraseña actual"
                      className="input-glass w-full text-sm pr-10"
                    />
                    <button onClick={() => setShowPin(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                      {showPin ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <input
                    type={showPin ? 'text' : 'password'}
                    value={pinForm.new}
                    onChange={e => setPinForm(f => ({ ...f, new: e.target.value }))}
                    placeholder="Nueva contraseña (mínimo 4 caracteres)"
                    className="input-glass w-full text-sm"
                  />
                  <input
                    type={showPin ? 'text' : 'password'}
                    value={pinForm.confirm}
                    onChange={e => setPinForm(f => ({ ...f, confirm: e.target.value }))}
                    placeholder="Confirmar nueva contraseña"
                    className="input-glass w-full text-sm"
                  />
                  <button onClick={changePin} disabled={pinLoading} className="btn-primary w-full flex items-center justify-center gap-2 text-sm">
                    {pinLoading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Check size={15} />}
                    {pinLoading ? 'Cambiando...' : 'Actualizar contraseña'}
                  </button>
                </div>
              </div>

              {/* 2FA */}
              <div className="glass-panel p-6 rounded-2xl space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-white font-semibold flex items-center gap-2"><Shield size={18} className="text-gray-400" />Autenticación de 2 factores</h2>
                  {twoFaStatus.enabled && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-green-500/20 text-green-400 font-medium">✓ Activo</span>
                  )}
                </div>

                <div className="p-3 bg-white/5 rounded-xl">
                  <p className="text-gray-400 text-sm">Protege tu cuenta con una capa extra de seguridad usando una aplicación autenticadora (Google Authenticator, Authy, etc.).</p>
                </div>

                {twoFaStatus.enabled ? (
                  <button onClick={disable2FA} className="w-full flex items-center justify-center gap-2 text-sm py-2.5 rounded-xl bg-red-500/15 hover:bg-red-500/25 text-red-400 border border-red-500/20 transition-all">
                    <Shield size={14} />Desactivar 2FA
                  </button>
                ) : !twoFaSetup ? (
                  <button onClick={setup2FA} disabled={twoFaLoading} className="btn-primary w-full flex items-center justify-center gap-2 text-sm">
                    {twoFaLoading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <QrCode size={15} />}
                    {twoFaLoading ? 'Generando...' : 'Activar 2FA'}
                  </button>
                ) : (
                  <div className="space-y-4">
                    <div className="text-center">
                      <p className="text-gray-400 text-sm mb-3">Escanea este código QR con tu app autenticadora:</p>
                      <img src={twoFaSetup.qr_url} alt="QR 2FA" className="w-40 h-40 mx-auto rounded-xl bg-white p-2" />
                      <div className="mt-3 p-3 bg-white/5 rounded-xl">
                        <p className="text-gray-500 text-xs mb-1">Clave manual:</p>
                        <code className="text-yellow-400 text-xs font-mono break-all">{twoFaSetup.secret}</code>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={twoFaCode}
                        onChange={e => setTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Código de 6 dígitos"
                        maxLength={6}
                        className="input-glass w-full text-sm text-center text-lg tracking-widest font-mono"
                      />
                      <button onClick={verify2FA} disabled={twoFaLoading} className="btn-primary w-full flex items-center justify-center gap-2 text-sm">
                        {twoFaLoading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Check size={15} />}
                        Verificar y activar
                      </button>
                      <button onClick={() => setTwoFaSetup(null)} className="w-full text-gray-500 text-xs hover:text-gray-400 transition-colors">Cancelar</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Connected devices / sessions */}
              <div className="glass-panel p-6 rounded-2xl space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-white font-semibold flex items-center gap-2"><Laptop size={18} className="text-gray-400" />Dispositivos conectados</h2>
                  <button onClick={loadSessions} className="text-gray-500 hover:text-gray-300 transition-colors">
                    <RefreshCw size={14} className={sessionsLoading ? 'animate-spin' : ''} />
                  </button>
                </div>

                {sessionsLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="w-6 h-6 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
                  </div>
                ) : sessions.length === 0 ? (
                  <div className="p-4 bg-white/5 rounded-xl text-center">
                    <p className="text-gray-500 text-sm">No hay sesiones activas registradas</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {sessions.map((s, i) => (
                      <div key={s.session_id || i} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                        <Laptop size={16} className="text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">{s.device_name || s.user_agent?.slice(0, 40) || 'Dispositivo desconocido'}</p>
                          <p className="text-gray-500 text-xs">{s.ip || '—'} · {s.last_seen ? new Date(s.last_seen).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</p>
                        </div>
                        {s.is_current ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 flex-shrink-0">Actual</span>
                        ) : (
                          <button onClick={() => revokeSession(s.session_id)} className="text-red-400 hover:text-red-300 transition-colors flex-shrink-0">
                            <LogOut size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                    {sessions.length > 1 && (
                      <button onClick={revokeAllSessions} className="w-full flex items-center justify-center gap-2 text-sm py-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/15 transition-all mt-2">
                        <LogOut size={14} />Cerrar todas las demás sesiones
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Danger zone */}
              <div className="p-4 bg-red-500/10 rounded-xl border border-red-500/20">
                <p className="text-red-400 text-sm font-medium mb-1 flex items-center gap-1.5"><AlertTriangle size={14} />Zona de peligro</p>
                <p className="text-gray-500 text-xs">Para eliminar tu cuenta o exportar tus datos, contacta a soporte en hola@okgo.mx.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsModule;
